// One sidebar row of the ChatShell's Project and Riwayat sections
// (design review ui.region_gate side_history: rows). An optional glyph, the
// label (a link when the row opens something), and a moreH Menu
// (ui/Menu.tsx) in its own lane, so the text never runs under it. The Menu
// always shows, quiet at rest and full ink on hover, focus, the current row
// and while open (design review menu_reveal: always_muted).
// Ganti nama (Riwayat) swaps the label for an inline input with Simpan and
// Batal; Enter saves, Escape cancels. Project renames through its dialog.
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Menu, type MenuItem } from "../ui/Menu";
import { Glyph, type GlyphName } from "../ui/icons";

export interface SideRowProps {
  label: string;
  /** A leading glyph (the folder of a project row). */
  icon?: GlyphName;
  /** The row opens this route; without it the row is a plain label. */
  href?: string;
  current?: boolean;
  /** Accessible name of the row's Menu, "Tindakan untuk <label>". */
  menuLabel: string;
  items: MenuItem[];
  renaming?: boolean;
  busy?: boolean;
  inputLabel?: string;
  maxLength?: number;
  onRename?: (next: string) => void;
  onCancelRename?: () => void;
}

export function SideRow({ label, icon, href, current, menuLabel, items, renaming = false, busy = false, inputLabel = "Nama", maxLength = 200, onRename, onCancelRename }: SideRowProps) {
  const [value, setValue] = useState(label);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!renaming) return;
    setValue(label);
    // After the menu returns focus to its trigger, move it into the field.
    const t = setTimeout(() => {
      input.current?.focus();
      input.current?.select?.();
    }, 0);
    return () => clearTimeout(t);
  }, [renaming, label]);

  const cancel = () => onCancelRename?.();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next = value.replace(/\s+/g, " ").trim();
    if (!next || next === label) {
      cancel();
      return;
    }
    onRename?.(next.slice(0, maxLength));
  };

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      cancel();
    }
  };

  if (renaming) {
    return (
      <li className="cs-row" data-renaming="">
        <form className="cs-rename" onSubmit={submit}>
          <input ref={input} className="cs-rename-input" aria-label={inputLabel} value={value} maxLength={maxLength} readOnly={busy} onChange={(e) => setValue(e.currentTarget.value)} onKeyDown={onKey} />
          <button type="submit" className="ui-icon-btn" aria-label="Simpan" title="Simpan" disabled={busy} aria-busy={busy || undefined}>
            <Glyph name="check" />
          </button>
          <button type="button" className="ui-icon-btn" aria-label="Batal" title="Batal" disabled={busy} onClick={cancel}>
            <Glyph name="closeCircle" />
          </button>
        </form>
      </li>
    );
  }

  const content = (
    <>
      {icon ? (
        <span className="cs-row-icon">
          <Glyph name={icon} size={18} />
        </span>
      ) : null}
      <span className="cs-row-text">{label}</span>
    </>
  );

  return (
    <li className="cs-row" data-current={current ? "" : undefined} data-icon={icon ? "" : undefined}>
      {href ? (
        <a className="cs-row-main" href={href} aria-current={current ? "page" : undefined} title={label}>
          {content}
        </a>
      ) : (
        <span className="cs-row-main" title={label}>
          {content}
        </span>
      )}
      <Menu label={menuLabel} items={items} className="cs-row-menu" />
    </li>
  );
}
